import { useEffect } from 'react';
import { Route, Routes } from 'react-router';
import { FinancialsTasks } from '../../components/financials-tasks/financials-tasks';
import { OutOfBloom } from '../../components/out-of-bloom/out-of-bloom';
import { ProjectProvider } from '../../contexts';
import { useFeatureFlag } from '../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../helpers/feature-flags/feature-flags.type';
import { clearCredentials, setSignUpInitialized } from '../../store/auth-store/auth-store.actions';
import { AddEditFinancials } from '../add-edit-financials/add-edit-financials';
import { Adherence } from '../adherence/adherence';
import { AgentMiner } from '../agent-miner/agent-miner';
import { SessionDetail } from '../agent-miner/session-detail/session-detail';
import { EditAgenticFinancialData } from '../agentic-process/agentic-financials/edit-agentic-financial-data';
import { AgenticProcess } from '../agentic-process/agentic-process';
import { AgenticProcessGraph } from '../agentic-process/agentic-process-graph/agentic-process-graph';
import { Calculations } from '../calculations/calculations';
import { DashboardTemplate } from '../dashboards/components/dashboard-template/dashboard-template';
import { Dashboard } from '../dashboards/dashboard';
import { Dashboards } from '../dashboards/dashboards';
import { DataManagement } from '../data-management/data-management';
import { Financials } from '../financials/financials';
import { Settings } from '../settings/settings';
import { FloraAgentLayout } from '../flora-agent/flora-agent';
import { FloraAsideSessionProvider } from '../flora/flora-aside-session.context';
import {
  FloraAgentAutomationBuilderRoute,
  FloraAgentAutomationRunsRoute,
  FloraAgentAutomationsRoute,
  FloraAgentChatRoute,
  FloraAgentCommandsRoute,
  FloraAgentCustomizeRoute,
  FloraAgentDocumentsRoute,
  FloraAgentGrantAccessRoute,
  FloraAgentIntegrationsRoute,
  FloraAgentSettingsRoute,
  FloraAgentPagesRoute,
  FloraAgentPageViewerRoute,
  FloraAgentPreferencesRoute,
} from '../flora-agent/flora-agent-routes';
import { AddNewIntegration } from '../integrations/add-new-integration/add-new-integration';
import { AddNewIntegrationAuthorize } from '../integrations/add-new-integration/add-new-integration-authorize';
import { AddNewIntegrationSuccess } from '../integrations/add-new-integration/add-new-integration-success';
import { TermsAndConditions } from '../legal/terms-and-conditions';
import { ProcessAnalysis } from '../process-analysis/process-analysis';

import { Process } from '../process/process';
import { TaskDetails } from '../process/task-details/task-details';
import { UnifiedProcessGraph } from '../process/unified-process-graph/unified-process-graph';
import { GeneralViewNames } from '../side-bar/side-bar.type';
import { EpicDetails } from '../strategy/epic-details/epic-details';
import { Epics } from '../strategy/epic-list/epics';
import { GoalDetails } from '../strategy/goals/goal-details';
import { Goals } from '../strategy/goals/goals';
import { InitiativePerformance } from '../strategy/initiative-performance/initiative-performance';
import { StrategyMindMap } from '../strategy/mind-map/mind-map';
import { OutcomeDetails } from '../strategy/outcomes/outcome-details';
import { Outcomes } from '../strategy/outcomes/outcomes';
import { StrategyOverview } from '../strategy/overview/overview';
import { StrategyRoadmap } from '../strategy/roadmap/roadmap';
import { Strategy } from '../strategy/strategy';
import { WorkPeriods } from '../work-periods/work-periods';
import styles from './navigation.module.css';

/**
 * How to add a new protected route:
 * (unprotected routes can be modified in the App component)
 *
 * Create a new entry in this list of routes.
 * 1. The view property is used to determine which navigation item is highlighted in the sidebar.
 *    (null can be passed if the route should not highlight any navigation item).
 * 2. The path property is used by the navigator to determine which route to render.
 *    Path must be unique.
 * 3. The element property is the component that will be rendered when the path is matched.
 */
export const ROUTES = [
  { view: GeneralViewNames.Adherence, path: 'adherence', element: <Adherence /> },
  { view: GeneralViewNames.Dashboards, path: 'dashboards', element: <Dashboards /> },
  { view: GeneralViewNames.Dashboards, path: 'dashboards/:dashboardId', element: <Dashboard /> },
  { view: GeneralViewNames.Dashboards, path: 'dashboards/template/:templateId', element: <DashboardTemplate /> },
  { view: GeneralViewNames.DataManagement, path: 'data-management', element: <DataManagement /> },
  { view: GeneralViewNames.Calculations, path: 'calculations', element: <Calculations /> },
  { view: GeneralViewNames.Strategy, path: 'strategy', element: <Strategy /> },
  { view: GeneralViewNames.Strategy, path: 'strategy/goals', element: <Goals /> },
  { view: GeneralViewNames.Strategy, path: 'strategy/goals/:id', element: <GoalDetails /> },
  { view: GeneralViewNames.Strategy, path: 'strategy/outcomes', element: <Outcomes /> },
  { view: GeneralViewNames.Strategy, path: 'strategy/outcomes/:id', element: <OutcomeDetails /> },
  { view: GeneralViewNames.Strategy, path: 'strategy/epics', element: <Epics /> },
  { view: GeneralViewNames.Strategy, path: 'strategy/epics/:id', element: <EpicDetails /> },
  { view: GeneralViewNames.Strategy, path: 'strategy/mind-map', element: <StrategyMindMap /> },
  { view: GeneralViewNames.Strategy, path: 'strategy/roadmap', element: <StrategyRoadmap /> },
  { view: GeneralViewNames.Strategy, path: 'strategy/overview', element: <StrategyOverview /> },
  { view: GeneralViewNames.WorkPeriods, path: 'work-periods', element: <WorkPeriods /> },
  {
    view: GeneralViewNames.Strategy,
    path: 'strategy/:portfolioId/:initiativeId',
    element: <InitiativePerformance />,
  },
  { view: GeneralViewNames.Process, path: 'process', element: <Process /> },
  { view: GeneralViewNames.Process, path: 'process/task-details', element: <TaskDetails /> },
  { view: GeneralViewNames.Process, path: 'process/unified', element: <UnifiedProcessGraph /> },
  { view: GeneralViewNames.Process, path: 'process/unified/:taskId', element: <UnifiedProcessGraph /> },
  { view: GeneralViewNames.AgentMiner, path: 'agent-miner', element: <AgentMiner /> },
  { view: GeneralViewNames.AgentMiner, path: 'agent-miner/sessions/:id', element: <SessionDetail /> },
  {
    view: GeneralViewNames.AgenticProcess,
    path: 'agentic-process',
    element: <AgenticProcess />,
  },
  {
    view: GeneralViewNames.AgenticProcess,
    path: 'agentic-process/graph',
    element: <AgenticProcessGraph />,
  },
  {
    view: GeneralViewNames.AgenticProcess,
    path: 'agentic-process/edit-agentic-financial-data',
    element: <EditAgenticFinancialData />,
  },
  {
    view: GeneralViewNames.ProcessAnalysis,
    path: 'process-analysis',
    element: <ProcessAnalysis />,
  },
  { view: GeneralViewNames.Integrations, path: 'integrations', element: <AddNewIntegration /> },
  { view: GeneralViewNames.Integrations, path: 'integrations/authorize', element: <AddNewIntegrationAuthorize /> },
  { view: GeneralViewNames.Integrations, path: 'integrations/success', element: <AddNewIntegrationSuccess /> },
  { view: GeneralViewNames.Financials, path: 'financials', element: <Financials /> },
  {
    view: GeneralViewNames.Financials,
    path: 'financials/portfolio/:portfolioId/add-edit-financials',
    element: <AddEditFinancials />,
  },
  {
    view: GeneralViewNames.Financials,
    path: 'financials/portfolio/:portfolioId/tasks',
    element: <FinancialsTasks />,
  },
  {
    view: GeneralViewNames.Flora,
    path: 'flora-agent',
    element: <FloraAgentLayout />,
    children: [
      { path: '', element: <FloraAgentChatRoute />, index: true },
      { path: ':chatId', element: <FloraAgentChatRoute /> },
      { path: 'commands', element: <FloraAgentCommandsRoute /> },
      { path: 'customize', element: <FloraAgentCustomizeRoute /> },
      { path: 'documents', element: <FloraAgentDocumentsRoute /> },
      { path: 'preferences', element: <FloraAgentPreferencesRoute /> },
      { path: 'integrations', element: <FloraAgentIntegrationsRoute /> },
      { path: 'settings', element: <FloraAgentSettingsRoute /> },
      { path: 'settings/:section', element: <FloraAgentSettingsRoute /> },
      { path: 'settings/connections/:appId', element: <FloraAgentSettingsRoute /> },
      { path: 'grant-access', element: <FloraAgentGrantAccessRoute /> },
      { path: 'automations', element: <FloraAgentAutomationsRoute /> },
      { path: 'automations/new', element: <FloraAgentAutomationBuilderRoute /> },
      { path: 'automations/:workflowId', element: <FloraAgentAutomationRunsRoute /> },
      { path: 'automations/:workflowId/edit', element: <FloraAgentAutomationBuilderRoute /> },
      { path: 'pages', element: <FloraAgentPagesRoute /> },
      { path: 'pages/:pageId', element: <FloraAgentPageViewerRoute /> },
    ],
  },
  { view: null, path: 'legal/terms-and-conditions', element: <TermsAndConditions /> },
  { view: null, path: 'settings', element: <Settings /> },
  { view: null, path: 'settings/:tab', element: <Settings /> },
];

export function NavigationContainer() {
  const isAgentMinerEnabled = useFeatureFlag(FeatureFlag.AgenticProcessByHooks);

  useEffect(() => {
    setSignUpInitialized(false);
    clearCredentials();
  }, []);

  const visibleRoutes = ROUTES.filter((route) => {
    if (route.view === GeneralViewNames.AgentMiner) return isAgentMinerEnabled;
    if (route.path === 'settings' || route.path === 'settings/:tab') return isAgentMinerEnabled;
    return true;
  });
  return (
    <div className={styles.navigationContainer}>
      <div className={styles.mainView}>
        {/* Above every page, so the right-dock Flora conversation survives page changes. */}
        <FloraAsideSessionProvider>
          <Routes>
            {visibleRoutes.map(({ path, element, children }) =>
              children ? (
                <Route key={path} path={path} element={<ProjectProvider>{element}</ProjectProvider>}>
                  {children.map((child, childIndex) =>
                    child.index ? (
                      <Route key={`${path}-index`} index element={child.element} />
                    ) : (
                      <Route
                        key={`${path}-${child.path ?? `child-${childIndex}`}`}
                        path={child.path}
                        element={child.element}
                      />
                    ),
                  )}
                </Route>
              ) : (
                <Route key={path} path={path} element={<ProjectProvider>{element}</ProjectProvider>} />
              ),
            )}
            {/* Default view to render incase of a 404 */}
            <Route path="*" element={<OutOfBloom />} />
          </Routes>
        </FloraAsideSessionProvider>
      </div>
    </div>
  );
}
